/**
 * Product Tracking Test Component
 * Simple test to verify product tracking functionality without database functions
 */

import React, { useState } from 'react';
import { useAnalytics } from '@/polymet/hooks/use-analytics';
import { useAuth } from '@/lib/auth-context';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

interface TestResult {
  test: string;
  status: 'pending' | 'success' | 'error';
  message: string;
  duration?: number;
}

export default function ProductTrackingTest() {
  const { user, isAuthenticated } = useAuth();
  const { trackProductVisit, getUserRecentlyVisitedProducts } = useAnalytics();
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);

  const addTestResult = (result: TestResult) => {
    setTestResults(prev => [...prev, result]);
  };

  const updateTestResult = (index: number, updates: Partial<TestResult>) => {
    setTestResults(prev => prev.map((result, i) => 
      i === index ? { ...result, ...updates } : result
    ));
  };

  const runTests = async () => {
    if (!isAuthenticated) {
      alert('Please log in to run product tracking tests');
      return;
    }

    setIsRunning(true);
    setTestResults([]);

    // Test 1: Basic Product View Tracking
    const test1Start = Date.now();
    addTestResult({
      test: 'Basic Product View Tracking',
      status: 'pending',
      message: 'Testing product view tracking...'
    });

    try {
      await trackProductVisit({
        productId: 'test-product-1',
        supplierId: 'test-supplier-1',
        categoryId: 'construction-equipment',
        subcategoryId: 'excavators',
        productCategoryId: 'medium-excavators',
        source: 'direct',
        interactionType: 'view',
        timeSpent: 45
      });

      updateTestResult(0, {
        status: 'success',
        message: 'Product view tracked successfully',
        duration: Date.now() - test1Start
      });
    } catch (error) {
      updateTestResult(0, {
        status: 'error',
        message: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        duration: Date.now() - test1Start
      });
    }

    // Test 2: Contact Interaction Tracking
    const test2Start = Date.now();
    addTestResult({
      test: 'Contact Interaction Tracking',
      status: 'pending',
      message: 'Testing contact interaction tracking...'
    });

    try {
      await trackProductVisit({
        productId: 'test-product-1',
        supplierId: 'test-supplier-1',
        categoryId: 'construction-equipment',
        subcategoryId: 'excavators',
        productCategoryId: 'medium-excavators',
        source: 'direct',
        interactionType: 'contact',
        timeSpent: 30
      });

      updateTestResult(1, {
        status: 'success',
        message: 'Contact interaction tracked successfully',
        duration: Date.now() - test2Start
      });
    } catch (error) {
      updateTestResult(1, {
        status: 'error',
        message: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        duration: Date.now() - test2Start
      });
    }

    // Test 3: RFQ Interaction Tracking
    const test3Start = Date.now();
    addTestResult({
      test: 'RFQ Interaction Tracking',
      status: 'pending',
      message: 'Testing RFQ interaction tracking...'
    });

    try {
      await trackProductVisit({
        productId: 'test-product-2',
        supplierId: 'test-supplier-2',
        categoryId: 'construction-equipment',
        subcategoryId: 'backhoe-loaders',
        productCategoryId: 'compact-backhoe-loaders',
        source: 'search',
        interactionType: 'rfq',
        timeSpent: 120
      });

      updateTestResult(2, {
        status: 'success',
        message: 'RFQ interaction tracked successfully',
        duration: Date.now() - test3Start
      });
    } catch (error) {
      updateTestResult(2, {
        status: 'error',
        message: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        duration: Date.now() - test3Start
      });
    }

    // Test 4: Recently Visited Products Retrieval
    const test4Start = Date.now();
    addTestResult({
      test: 'Recently Visited Products Retrieval',
      status: 'pending',
      message: 'Testing recently visited products retrieval...'
    });

    try {
      const recentProducts = await getUserRecentlyVisitedProducts(5);
      
      updateTestResult(3, {
        status: 'success',
        message: `Retrieved ${recentProducts.length} recently visited products`,
        duration: Date.now() - test4Start
      });
    } catch (error) {
      updateTestResult(3, {
        status: 'error',
        message: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        duration: Date.now() - test4Start
      });
    }

    // Test 5: Multiple Visits to Same Product (Aggregation Test)
    const test5Start = Date.now();
    addTestResult({
      test: 'Multiple Visits Aggregation',
      status: 'pending',
      message: 'Testing multiple visits to same product...'
    });

    try {
      // Visit the same product multiple times
      await trackProductVisit({
        productId: 'test-product-3',
        supplierId: 'test-supplier-3',
        categoryId: 'construction-equipment',
        source: 'category',
        interactionType: 'view',
        timeSpent: 60
      });

      await trackProductVisit({
        productId: 'test-product-3',
        supplierId: 'test-supplier-3',
        categoryId: 'construction-equipment',
        source: 'category',
        interactionType: 'view',
        timeSpent: 90
      });

      updateTestResult(4, {
        status: 'success',
        message: 'Multiple visits tracked and aggregated successfully',
        duration: Date.now() - test5Start
      });
    } catch (error) {
      updateTestResult(4, {
        status: 'error',
        message: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        duration: Date.now() - test5Start
      });
    }

    // Test 6: RFQ Functionality Test
    const test6Start = Date.now();
    addTestResult({
      test: 'RFQ Quote Request Functionality',
      status: 'pending',
      message: 'Testing RFQ creation with prefilled product data...'
    });

    try {
      // Simulate creating an RFQ for a specific product
      const testProduct = {
        productId: 'test-product-rfq',
        productName: 'CAT 320D2 Excavator',
        supplierId: 'test-supplier-cat',
        supplierName: 'CAT Equipment Dealer',
        categoryId: 'construction-equipment',
        subcategoryId: 'excavators',
        productCategoryId: 'medium-excavators',
        manufacturer: 'Caterpillar',
        model: '320D2',
        year: '2020',
        condition: 'Used',
        price: '2500000',
        currency: 'INR'
      };

      // Test that the RFQ URL is properly constructed
      const queryParams = new URLSearchParams();
      Object.entries(testProduct).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
      
      const rfqUrl = `/create-rfq?${queryParams.toString()}`;
      
      // Track the RFQ interaction
      await trackProductVisit({
        productId: testProduct.productId,
        supplierId: testProduct.supplierId,
        categoryId: testProduct.categoryId,
        subcategoryId: testProduct.subcategoryId,
        productCategoryId: testProduct.productCategoryId,
        source: 'direct',
        interactionType: 'rfq',
        timeSpent: 150
      });

      updateTestResult(5, {
        status: 'success',
        message: `RFQ URL generated successfully: ${rfqUrl.length} characters`,
        duration: Date.now() - test6Start
      });
    } catch (error) {
      updateTestResult(5, {
        status: 'error',
        message: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        duration: Date.now() - test6Start
      });
    }

    setIsRunning(false);
  };

  const clearResults = () => {
    setTestResults([]);
  };

  const getStatusIcon = (status: TestResult['status']) => {
    switch (status) {
      case 'pending':
        return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-red-500" />;
    }
  };

  const getStatusBadge = (status: TestResult['status']) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="text-blue-600">Running</Badge>;
      case 'success':
        return <Badge variant="default" className="bg-green-600">Passed</Badge>;
      case 'error':
        return <Badge variant="destructive">Failed</Badge>;
    }
  };

  if (!isAuthenticated) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Product Tracking Test</CardTitle>
          <CardDescription>
            Please log in to test product tracking functionality
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertDescription>
              Authentication required to run product tracking tests.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Product Tracking System Test</CardTitle>
          <CardDescription>
            Test the product tracking functionality without database functions.
            All logic runs in TypeScript backend code.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 mb-6">
            <Button 
              onClick={runTests} 
              disabled={isRunning}
              className="flex items-center gap-2"
            >
              {isRunning && <Loader2 className="h-4 w-4 animate-spin" />}
              {isRunning ? 'Running Tests...' : 'Run All Tests'}
            </Button>
            <Button 
              variant="outline" 
              onClick={clearResults} 
              disabled={isRunning}
            >
              Clear Results
            </Button>
          </div>

          {user && (
            <Alert className="mb-6">
              <AlertDescription>
                <strong>Logged in as:</strong> {user.email || user.phone || user.id}
              </AlertDescription>
            </Alert>
          )}

          {testResults.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Test Results</h3>
              {testResults.map((result, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between p-4 border rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    {getStatusIcon(result.status)}
                    <div>
                      <div className="font-medium">{result.test}</div>
                      <div className="text-sm text-muted-foreground">
                        {result.message}
                        {result.duration && (
                          <span className="ml-2">({result.duration}ms)</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {getStatusBadge(result.status)}
                </div>
              ))}
            </div>
          )}

          {testResults.length === 0 && !isRunning && (
            <div className="text-center py-8 text-muted-foreground">
              Click "Run All Tests" to test the product tracking functionality
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>What These Tests Check</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-semibold mb-2">Backend Logic Tests:</h4>
              <ul className="text-sm space-y-1 text-muted-foreground">
                <li>• Product view tracking with timestamps</li>
                <li>• Contact interaction recording</li>
                <li>• RFQ request tracking</li>
                <li>• Data retrieval and aggregation</li>
                <li>• Multiple visits to same product</li>
                <li>• RFQ functionality with prefilled data</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Database Operations:</h4>
              <ul className="text-sm space-y-1 text-muted-foreground">
                <li>• Insert new analytics records</li>
                <li>• Update existing visit counts</li>
                <li>• Handle duplicate prevention</li>
                <li>• Timestamp management in code</li>
                <li>• RLS policy compliance</li>
                <li>• RFQ URL parameter handling</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 